import React from 'react';
import { ActionButton } from 'equal-ds-ui';
import { X, Info } from 'lucide-react';
import type { AdminUserTableRow } from '../../../types/admin-users';
import approveTickIcon from '../../../assets/images/approve_tick_icon.png';
import redCrossIcon from '../../../assets/images/red_cross_icon.png';

export type ApprovalAction = 'APPROVE' | 'REJECT';

export interface AdminUserApprovalDialogProps {
  user: AdminUserTableRow | null;
  action: ApprovalAction;
  isOpen: boolean;
  onConfirm: () => Promise<void> | void;
  onClose: () => void;
  isProcessing?: boolean;
}

export const AdminUserApprovalDialog: React.FC<AdminUserApprovalDialogProps> = ({
  user,
  action,
  isOpen,
  onConfirm,
  onClose,
  isProcessing = false,
}) => {
  if (!isOpen || !user) {
    return null;
  }

  const isApprove = action === 'APPROVE';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-md bg-white rounded-xl shadow-xl border border-border-default p-6">
        {/* Header with close button */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-text-primary">
            {isApprove ? 'Approve User' : 'Reject User'}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-gray-50 hover:bg-gray-200 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Icon */}
        <div className="flex justify-center mb-5">
          <img
            src={isApprove ? approveTickIcon : redCrossIcon}
            alt=""
            className="w-20 h-20 object-contain"
          />
        </div>

        {/* Message */}
        <p className="text-sm text-text-secondary text-center mb-2">
          Are you sure you want to {isApprove ? 'approve' : 'reject'}{' '}
          <span className="font-semibold text-text-primary">{user.name}</span>?
        </p>

        <p className="text-xs text-text-secondary text-center mb-4">
          {isApprove
            ? 'Approving will activate the account and notify the user.'
            : 'Rejecting will notify the requestor and keep the account inactive.'}
        </p>

        {/* Warning note */}
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 mb-6">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>Please note that this action cannot be undone.</span>
        </div>

        {/* Action button */}
        <div className="equal-ds-root">
          <ActionButton
            intent={isApprove ? 'approve' : 'reject'}
            size="md"
            filled
            onClick={onConfirm}
            disabled={isProcessing}
            className="w-full"
          >
            {isProcessing ? 'Processing...' : isApprove ? 'Approve' : 'Reject'}
          </ActionButton>
        </div>
      </div>
    </div>
  );
};
